class CartDrawer extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) {
      overlay.addEventListener('click', this.close.bind(this));
    }

    this.setHeaderCartIconAccessibility();

    // Sync on page load/restore to handle cache variations (bfcache)
    window.addEventListener('pageshow', () => {
      this.syncCartDrawer();
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
    // Pre-fetch in background on hover
    cartLink.addEventListener('mouseenter', () => {
      this.syncCartDrawer();
    });
  }

  syncCartDrawer(force = false) {
    if (this.classList.contains('active') && !force) return Promise.resolve();
    const now = Date.now();
    if (!force && this.lastSyncTime && now - this.lastSyncTime < 8000) return Promise.resolve();
    this.lastSyncTime = now;
    const requestId = this.syncRequestId = (this.syncRequestId || 0) + 1;

    const cartDrawerUrl = (window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart');
    return fetch(`${cartDrawerUrl}?sections=cart-drawer`)
      .then((response) => response.json())
      .then((parsedState) => {
        if (requestId !== this.syncRequestId || (this.classList.contains('active') && !force)) return;
        const html = parsedState['cart-drawer'];
        if (html) {
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const newInner = parsed.querySelector('#CartDrawer');
          const currentInner = this.querySelector('#CartDrawer');
          if (newInner && currentInner) {
            currentInner.innerHTML = newInner.innerHTML;
            
            // Re-bind overlay event listener since innerHTML was replaced
            const overlay = this.querySelector('#CartDrawer-Overlay');
            if (overlay) {
              overlay.addEventListener('click', this.close.bind(this));
            }
            
            // Sync is-empty class
            const newCartDrawer = parsed.querySelector('cart-drawer');
            const isEmpty = newCartDrawer ? newCartDrawer.classList.contains('is-empty') : true;
            this.classList.toggle('is-empty', isEmpty);
          }
        }
      })
      .catch((err) => console.error('Error pre-fetching cart drawer:', err));
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    
    // Force immediate reflow and add classes synchronously for instant visual feedback
    this.offsetHeight; // force reflow
    this.classList.add('animate', 'active');

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.syncRequestId = (this.syncRequestId || 0) + 1;
    this.productId = parsedState.id;

    if (parsedState && parsedState.sections) {
      this.getSectionsToRender().forEach((section => {
        const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
        if (sectionElement && parsedState.sections[section.id]) {
          const sectionHtml = parsedState.sections[section.id];
          const innerHtml = this.getSectionInnerHTML(sectionHtml, section.selector);
          if (innerHtml !== null && innerHtml !== undefined) {
            sectionElement.innerHTML = innerHtml;
          }

          if (section.id === 'cart-drawer') {
            const parsedDOM = new DOMParser().parseFromString(sectionHtml, 'text/html');
            const newCartDrawer = parsedDOM.querySelector('cart-drawer');
            const isEmpty = newCartDrawer ? newCartDrawer.classList.contains('is-empty') : false;
            
            this.classList.toggle('is-empty', isEmpty);
            
            const drawerInner = this.querySelector('.drawer__inner');
            if (drawerInner) drawerInner.classList.toggle('is-empty', isEmpty);

            const drawerItems = this.querySelector('cart-drawer-items');
            const newDrawerItems = parsedDOM.querySelector('cart-drawer-items');
            if (drawerItems) {
              drawerItems.classList.toggle('is-empty', newDrawerItems ? newDrawerItems.classList.contains('is-empty') : isEmpty);
            }

            const drawerFooter = this.querySelector('.drawer__footer');
            const newDrawerFooter = parsedDOM.querySelector('.drawer__footer');
            if (drawerFooter) {
              drawerFooter.classList.toggle('is-empty', newDrawerFooter ? newDrawerFooter.classList.contains('is-empty') : isEmpty);
            }
          }
        }
      }));
    }

    if (window.updateDeliveryPreview) window.updateDeliveryPreview(0);

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) {
        overlay.addEventListener('click', this.close.bind(this));
      }
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return '';
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const target = selector ? parsed.querySelector(selector) : parsed.querySelector('.shopify-section');
    if (target) return target.innerHTML;
    return parsed.body ? parsed.body.innerHTML : html;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
